import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock, Download } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';
import { getVideoBySlug, formatDuration } from '../data/videoIndex';

// A watch page: one video, and the video is the page.
//
// Search Console rejected every embed on the site with "Is on a watch page?
// No - the video is supplementary content on the page". That verdict is about
// the PAGE, not the player's position: hoisting the iframe up the glossary
// template on 2026-08-15 did not change it. So the layout rules here are load
// bearing and should not be "improved" casually:
//
//   1. The h1 is the video title, not a topic name.
//   2. The iframe is the very next element. Nothing goes between them: no
//      definition callout, no metadata row, no share buttons, no ad slot.
//   3. Everything below is ABOUT the video (description, transcript). No table
//      of contents, no article body, no FAQ block.
//   4. The transcript renders expanded, not inside <details>. Collapsed text
//      is weaker evidence that the page is about the video, and it is the
//      main body copy here.
//
// Related reading gets one link at the bottom, deliberately after the
// transcript, so the page does not read as an article with a video on top.
const VideoWatchPage = () => {
  const { videoSlug } = useParams();
  const video = getVideoBySlug(videoSlug);

  if (!video) {
    return <Navigate to="/videos" replace />;
  }

  const canonical = `https://www.getsortio.com/videos/${video.slug}`;
  const thumbnail = `https://img.youtube.com/vi/${video.youtubeId}/maxresdefault.jpg`;
  const readable = formatDuration(video.duration);

  // Mirrors buildVideoObject() in scripts/generate-snap-routes.js, which is
  // the copy that actually ships (stamp-meta.js strips component-rendered
  // JSON-LD at postbuild). Keep the two in sync; this one serves the dev
  // server and any client-side navigation.
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: video.title,
      description: video.description,
      thumbnailUrl: [thumbnail],
      uploadDate: video.uploadDate,
      ...(video.duration ? { duration: video.duration } : {}),
      contentUrl: `https://www.youtube.com/watch?v=${video.youtubeId}`,
      embedUrl: `https://www.youtube.com/embed/${video.youtubeId}`,
      url: canonical,
      ...(video.transcript ? { transcript: video.transcript } : {}),
      publisher: {
        '@type': 'Organization',
        name: 'Sortio',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.getsortio.com/logo512.png',
        },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
        { '@type': 'ListItem', position: 2, name: 'Videos', item: 'https://www.getsortio.com/videos' },
        { '@type': 'ListItem', position: 3, name: video.title, item: canonical },
      ],
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#121212' }}>
      <SEOHead
        title={`${video.title} | Sortio`}
        description={video.description}
        canonical={canonical}
        schema={schema}
        ogImage={thumbnail}
        lastModified={video.uploadDate}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs
          items={[
            { label: 'Videos', href: '/videos' },
            { label: video.title },
          ]}
        />

        <h1 className="text-3xl md:text-4xl font-semibold text-apple-50 mb-6">
          {video.title}
        </h1>

        {/* The player. First thing under the h1, eagerly loaded, 16:9. */}
        <div
          className="relative w-full overflow-hidden rounded-2xl border border-apple-700/40 shadow-lg"
          style={{ paddingTop: '56.25%' }}
        >
          <iframe
            className="absolute inset-0 h-full w-full"
            src={`https://www.youtube.com/embed/${video.youtubeId}?rel=0`}
            title={video.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-apple-400">
          {readable && (
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              {readable}
            </span>
          )}
          {video.uploadDate && (
            <span>Published {new Date(video.uploadDate).toLocaleDateString()}</span>
          )}
          <a
            href={`https://www.youtube.com/watch?v=${video.youtubeId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-sortio-primary transition-colors"
          >
            Watch on YouTube
          </a>
        </div>

        <p className="mt-6 text-lg text-apple-200 leading-relaxed">
          {video.description}
        </p>

        {video.transcript && (
          <section className="mt-12">
            <h2 className="text-2xl font-semibold text-apple-50 mb-6">Transcript</h2>
            <div className="space-y-4 text-apple-300 leading-relaxed">
              {video.transcript.split('\n\n').map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </section>
        )}

        <div className="mt-12 rounded-2xl border border-sortio-primary/20 bg-sortio-primary/10 p-6">
          <h2 className="text-xl font-semibold text-apple-50 mb-2">
            Try it on your own folder
          </h2>
          <p className="text-apple-300 mb-4">
            Sortio is free to try on Mac, Windows, and Linux. Every move is
            previewed before anything is touched, and you can undo a sort.
          </p>
          <Link
            to="/download"
            className="inline-flex items-center px-5 py-2.5 bg-sortio-primary text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            <Download className="mr-2" size={16} />
            Download Sortio
          </Link>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 pt-8 border-t border-apple-700/40">
          <Link
            to="/videos"
            className="flex items-center text-apple-400 hover:text-sortio-primary transition-colors"
          >
            <ArrowLeft className="mr-2" size={16} />
            All videos
          </Link>
          <Link
            to={video.articlePath}
            className="flex items-center text-apple-400 hover:text-sortio-primary transition-colors group"
          >
            Read the written guide
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VideoWatchPage;
